import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Field as FormField, useField } from 'react-final-form';
import styled from '@emotion/styled';
import find from 'lodash/find';
import orderBy from 'lodash/orderBy';
import noop from 'lodash/noop';
import get from 'lodash/get';

import { ClickOutside } from '@vmos2/utils';
import { useDeviceSize } from '@vmos2/providers';
import { Icon } from '../icons/Icon';
import { FlagIcon } from '../icons';

type Props = {
  phonePrefixData: mixed[],
  defaultPhonePrefix: string,
  changeHandler: ?() => void,
};

export const PhonePrefixSelector = ({ phonePrefixData, defaultPhonePrefix, changeHandler }: Props) => {
  const { isLargeKioskScreen } = useDeviceSize();
  const listRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState(phonePrefixData[0]);
  const [listData, setListData] = useState(phonePrefixData);
  const toggle = useCallback(() => setOpen(open => !open), []);
  const field = useField('phonePrefix');
  const allowManyCountries = listData.length > 1;

  useEffect(() => {
    if (phonePrefixData && defaultPhonePrefix) {
      const defaultCountry = find(phonePrefixData, c => c.phonePrefix.replace('+', '') === defaultPhonePrefix);
      if (defaultCountry) setSelection(defaultCountry);
      field.input.onChange(get(defaultCountry, 'phonePrefix', '').replace('+', ''));
    }
  }, [phonePrefixData, defaultPhonePrefix]);

  useEffect(() => {
    const sorted = orderBy(phonePrefixData, c => (c.code === selection.code ? -1 : 0), ['asc']);
    setListData(sorted);
  }, [selection]);


  const handleOnClick = item => {
    setSelection(item);
    changeHandler(item);
    setOpen(false);
    field.input.onChange(item.phonePrefix.replace('+', ''));
  };

  const scrollToCountry = ({ keyCode }) => {
    const letter = String.fromCharCode(keyCode).toUpperCase();

    const targetCountryElement = Array.from(listRef.current.children).find(child =>
      child.lastChild.innerHTML.startsWith(letter)
    );

    if (targetCountryElement) {
      listRef.current.scrollTop = targetCountryElement.offsetTop;
    }
  };

  useEffect(() => {
    if (open) listRef.current.focus();
  }, [open]);

  const dropdownList = useMemo(
    () => (
      <List tabIndex={0} ref={listRef} onKeyDown={scrollToCountry}>
        {listData.map(item => (
          <ListItem key={item.code} onClick={() => handleOnClick(item)}>
            <StyledFlag countryCode={item.code} />
            <span>{`${item.country} ${item.phonePrefix}`}</span>
          </ListItem>
        ))}
      </List>
    ),
    [listData, open]
  );

  return (
    <>
      <ClickOutside onClickOutside={() => setOpen(false)}>
        <Wrapper
          isLargeKioskScreen={isLargeKioskScreen}
          tabIndex={0}
          onKeyPress={toggle}
          onClick={allowManyCountries ? toggle : undefined}
          allowManyCountries={allowManyCountries}
        >
          {selection && (
            <SelectedItem>
              <StyledFlag countryCode={selection.code} />
              {allowManyCountries && <ChevronDown name="ChevronDown" />}
              {selection.phonePrefix}
            </SelectedItem>
          )}
        </Wrapper>
        {open && dropdownList}
      </ClickOutside>

      <FormField component={HiddenInput} name="phonePrefix" />
    </>
  );
};

const HiddenInput = styled('input')`
  display: none;
`;

const Wrapper = styled('div')`
  display: flex;
  height: 100%;
  outline: none;
  position: relative;
  width: max-content;
  cursor: ${({ allowManyCountries }) => (allowManyCountries ? 'pointer' : 'auto')};
  padding-left: 16px;
`;

const SelectedItem = styled('div')`
  display: flex;
  align-items: center;
  line-height: 1;

  div {
    border: 0;
  }

  &:focus {
    outline: 0;
  }
`;

const List = styled('ul')`
  position: absolute;
  left: 0;
  right: 0;
  top: 24px;
  padding: 0;
  max-height: 316px;
  outline: none;
  overflow-y: scroll;
  border: ${({ theme }) => theme.stroke.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.boxShadow.medium};
  z-index: 1;

  ::-webkit-scrollbar {
    width: 0;
  }
`;

const ListItem = styled('li')`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.color.white};
  padding: 10px 16px;
  height: 44px;
  &:hover,
  &:focus {
    cursor: pointer;
    background-color: ${({ theme }) => theme.color.lighterGrey};
  }

  &:first-of-type {
    border-bottom: ${({ theme }) => theme.stroke.light};
  }
`;

const ChevronDown = styled(Icon)`
  margin: auto 8px auto 0;
  pointer-events: none;
  fill: ${({ theme }) => theme.color.grey};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const StyledFlag = styled(FlagIcon)`
  width: 18px;
  margin-right: 8px;
`;

PhonePrefixSelector.defaultProps = {
  changeHandler: noop,
  phonePrefixData: [],
};
